import cloudTextureUrl from 'assets/images/png_4_f0ed118105.png';
import * as THREE from 'three';
import { easeInOutCubic } from 'utils/galaxy/interpolation';

import { EngineConfig, ViewMode } from '../types';

interface GasCloudData {
  angle: number;
  distance: number;
  baseY: number;
  origAngle: number;
  origDistance: number;
  origBaseY: number;
  rotationSpeed: number;
  breathPhase: number;
  breathSpeed: number;
  transFromX: number;
  transFromY: number;
  transFromZ: number;
  transToX: number;
  transToY: number;
  transToZ: number;
}

/**
 * Background stars + nebula (gas clouds).
 * Ported from: helpers/backgroundStars.ts, helpers/nebula.ts, useNebulaAnimation.ts
 *
 * Nebula clouds transition between view modes alongside stars (calcNebulaTargets).
 */
export class BackgroundSystem {
  private nebula: THREE.Group;
  private elapsed = 0;

  // Transition state (driven by StarsSystem via SpaceEngine)
  private transitioning = false;
  private transitionProgress = 0;
  private currentViewMode: ViewMode = 'spiral';
  private smoothRotation = false;

  constructor(
    private scene: THREE.Scene,
    private config: EngineConfig
  ) {
    this.createBackgroundStars();
    this.nebula = this.createNebula(config);
  }

  /** Sync nebula group Y-rotation with star group (for non-spiral/tiers modes) */
  setRotationY(y: number): void {
    this.nebula.rotation.y = y;
  }

  /** Start cloud transition to a new view mode (called from SpaceEngine alongside StarsSystem) */
  switchView(mode: ViewMode): void {
    this.calcNebulaTargets(mode);
    this.transitioning = true;
    this.transitionProgress = 0;
    this.currentViewMode = mode;
  }

  setSmoothRotation(enabled: boolean): void {
    this.smoothRotation = enabled;
  }

  /** Bake accumulated group rotation.y into individual cloud positions + angles */
  bakeGroupRotation(): void {
    const rotY = this.nebula.rotation.y;
    if (Math.abs(rotY) < 0.001) {
      this.nebula.rotation.y = 0;
      return;
    }

    const cosR = Math.cos(rotY);
    const sinR = Math.sin(rotY);

    for (let c = 0; c < this.nebula.children.length; c++) {
      const layer = this.nebula.children[c];
      if (!(layer instanceof THREE.Points) || !layer.geometry?.userData?.gasData) continue;

      const positions = layer.geometry.attributes.position.array as Float32Array;
      const gasData = layer.geometry.userData.gasData as GasCloudData[];

      for (let i = 0; i < gasData.length; i++) {
        const cloud = gasData[i];
        cloud.angle += rotY;

        const x = positions[i * 3];
        const z = positions[i * 3 + 2];
        positions[i * 3] = x * cosR + z * sinR;
        positions[i * 3 + 2] = -x * sinR + z * cosR;

        // Bake into transFrom too (used during transition lerp)
        const fx = cloud.transFromX;
        const fz = cloud.transFromZ;
        cloud.transFromX = fx * cosR + fz * sinR;
        cloud.transFromZ = -fx * sinR + fz * cosR;
      }
      layer.geometry.attributes.position.needsUpdate = true;
    }
    this.nebula.rotation.y = 0;
  }

  animate(dt: number): void {
    this.elapsed += dt;
    const elapsed = this.elapsed;
    const step = this.smoothRotation ? dt * 60 : 1.0;

    for (let c = 0; c < this.nebula.children.length; c++) {
      const layer = this.nebula.children[c];

      if (layer instanceof THREE.Points && layer.geometry?.userData?.gasData) {
        const positions = layer.geometry.attributes.position.array as Float32Array;
        const gasData = layer.geometry.userData.gasData as GasCloudData[];

        if (this.transitioning) {
          // Lerp cloud positions alongside stars
          const nt = easeInOutCubic(Math.min(this.transitionProgress, 1));
          for (let i = 0; i < gasData.length; i++) {
            const cloud = gasData[i];
            positions[i * 3] = cloud.transFromX + (cloud.transToX - cloud.transFromX) * nt;
            positions[i * 3 + 1] = cloud.transFromY + (cloud.transToY - cloud.transFromY) * nt;
            positions[i * 3 + 2] = cloud.transFromZ + (cloud.transToZ - cloud.transFromZ) * nt;
          }
        } else if (this.currentViewMode === 'spiral' || this.currentViewMode === 'tiers') {
          // Individual orbital rotation + breathing.
          // Per-frame increment (no dt, no mobile multiplier) — matches original app.js line 4910.
          for (let i = 0; i < gasData.length; i++) {
            const cloud = gasData[i];
            cloud.angle += cloud.rotationSpeed * step;
            positions[i * 3] = Math.cos(cloud.angle) * cloud.distance;
            positions[i * 3 + 2] = Math.sin(cloud.angle) * cloud.distance;
            positions[i * 3 + 1] =
              cloud.baseY + Math.sin(elapsed * cloud.breathSpeed + cloud.breathPhase) * 0.15;
          }
        } else {
          // Other views: gentle breathing around target Y, group rotation handles XZ
          for (let i = 0; i < gasData.length; i++) {
            const cloud = gasData[i];
            positions[i * 3 + 1] =
              cloud.transToY + Math.sin(elapsed * cloud.breathSpeed + cloud.breathPhase) * 0.3;
          }
        }
        layer.geometry.attributes.position.needsUpdate = true;
      } else if (layer.userData.rotationSpeed) {
        layer.rotation.y += layer.userData.rotationSpeed * step;
      }
    }

    // Advance transition progress (synced with StarsSystem speed)
    if (this.transitioning) {
      this.transitionProgress += 0.012 * dt * 60;
      if (this.transitionProgress >= 1) {
        this.transitionProgress = 1;
        this.transitioning = false;

        // For spiral/tiers: sync angles with final positions
        if (this.currentViewMode === 'spiral' || this.currentViewMode === 'tiers') {
          this.syncAnglesAfterTransition();
        }
      }
    }
  }

  destroy(): void {
    this.scene.remove(this.nebula);
    this.nebula.traverse((obj) => {
      if (obj instanceof THREE.Points) {
        obj.geometry.dispose();
        (obj.material as THREE.Material).dispose();
      }
    });
  }

  // --- View mode target calculations (ported from app.js calcNebulaTargets) ---

  private calcNebulaTargets(viewMode: ViewMode): void {
    for (let c = 0; c < this.nebula.children.length; c++) {
      const layer = this.nebula.children[c];
      if (!(layer instanceof THREE.Points) || !layer.geometry?.userData?.gasData) continue;

      const gasData = layer.geometry.userData.gasData as GasCloudData[];
      const positions = layer.geometry.attributes.position.array as Float32Array;
      const count = gasData.length;

      // Store current positions as "from"
      for (let i = 0; i < count; i++) {
        const cloud = gasData[i];
        cloud.transFromX = positions[i * 3];
        cloud.transFromY = positions[i * 3 + 1];
        cloud.transFromZ = positions[i * 3 + 2];
      }

      // Compute "to" based on view mode
      for (let i = 0; i < count; i++) {
        const cloud = gasData[i];
        const t = i / count; // normalized rank 0..1

        if (viewMode === 'spiral') {
          cloud.transToX = Math.cos(cloud.origAngle) * cloud.origDistance;
          cloud.transToY = cloud.origBaseY;
          cloud.transToZ = Math.sin(cloud.origAngle) * cloud.origDistance;
        } else if (viewMode === 'tiers') {
          const tier = Math.floor(t * 5);
          const ringRadii = [14, 28, 44, 60, 78];
          const ringWidth = [6, 8, 10, 10, 12];
          const r = ringRadii[tier] + (Math.random() - 0.5) * ringWidth[tier];
          const angle = cloud.origAngle + tier * 0.618;
          cloud.transToX = Math.cos(angle) * r;
          cloud.transToY = (Math.random() - 0.5) * 2;
          cloud.transToZ = Math.sin(angle) * r;
        } else if (viewMode === 'scatter') {
          const extent = this.config.galaxyRadius * 0.9;
          cloud.transToX = (Math.random() - 0.5) * extent * 2;
          cloud.transToY = (Math.random() - 0.5) * 1.5;
          cloud.transToZ = (Math.random() - 0.5) * extent * 2;
        } else if (viewMode === 'helix') {
          const turns = 12,
            helixR = 30,
            maxH = 60;
          const angle = t * turns * Math.PI * 2;
          const r = helixR + t * 15 + (Math.random() - 0.5) * 10;
          cloud.transToX = Math.cos(angle) * r;
          cloud.transToY = maxH / 2 - t * maxH;
          cloud.transToZ = Math.sin(angle) * r;
        } else if (viewMode === 'sphere') {
          const radius = 55,
            height = 110;
          const y = radius - t * height;
          const yNorm = y / radius;
          const sliceR = radius * Math.sqrt(Math.max(0, 1 - yNorm * yNorm));
          const goldenA = Math.PI * (3 - Math.sqrt(5));
          const theta = i * goldenA;
          const r = sliceR * (0.85 + Math.random() * 0.15);
          cloud.transToX = Math.cos(theta) * r;
          cloud.transToY = y;
          cloud.transToZ = Math.sin(theta) * r;
        } else if (viewMode === 'tornado') {
          const maxR = 65,
            minR = 4,
            h = 80;
          const r = maxR * Math.pow(1 - t, 0.6) + minR + (Math.random() - 0.5) * 5;
          const spiralT = 3 + t * 12;
          const angle = spiralT * Math.PI * 2 * t;
          cloud.transToX = Math.cos(angle) * r;
          cloud.transToY = h / 2 - t * h;
          cloud.transToZ = Math.sin(angle) * r;
        } else if (viewMode === 'grid') {
          const cols = Math.ceil(Math.sqrt(count * 1.5));
          const rows = Math.ceil(count / cols);
          const gridExtent = 120;
          const spacingX = gridExtent / Math.max(cols - 1, 1);
          const spacingZ = gridExtent / Math.max(rows - 1, 1);
          const col = i % cols,
            row = Math.floor(i / cols);
          cloud.transToX = col * spacingX - gridExtent / 2;
          cloud.transToY = (Math.random() - 0.5) * 0.3;
          cloud.transToZ = row * spacingZ - gridExtent / 2;
        } else if (viewMode === 'sunflower') {
          const goldenA = Math.PI * (3 - Math.sqrt(5));
          const maxR = 65;
          const r = maxR * Math.sqrt(t) + (Math.random() - 0.5) * 2;
          const angle = i * goldenA;
          cloud.transToX = Math.cos(angle) * r;
          cloud.transToY = (Math.random() - 0.5) * 1;
          cloud.transToZ = Math.sin(angle) * r;
        } else if (viewMode === 'spring') {
          const turns = 8,
            springR = 28,
            h = 100;
          const angle = t * turns * Math.PI * 2;
          const r = springR + (Math.random() - 0.5) * 8;
          cloud.transToX = Math.cos(angle) * r;
          cloud.transToY = h / 2 - t * h;
          cloud.transToZ = Math.sin(angle) * r;
        } else if (viewMode === 'dna') {
          const amp = 30,
            depth = 10,
            h = 130,
            turns = 6;
          const phase = i % 2 === 0 ? 0 : Math.PI;
          const angle = t * turns * Math.PI * 2 + phase;
          const y = h / 2 - t * h;
          cloud.transToX = amp * Math.sin(angle) + (Math.random() - 0.5) * 2;
          cloud.transToY = y;
          cloud.transToZ = depth * Math.cos(angle) + (Math.random() - 0.5) * 2;
        } else if (viewMode === 'clusters') {
          const K = 8;
          const ci = i % K;
          const cAngle = (ci / K) * Math.PI * 2;
          const cx = Math.cos(cAngle) * 45,
            cz = Math.sin(cAngle) * 45;
          const localR = 12 * Math.sqrt(Math.random());
          const a = i * Math.PI * (3 - Math.sqrt(5));
          cloud.transToX = cx + Math.cos(a) * localR;
          cloud.transToY = (Math.random() - 0.5) * 6;
          cloud.transToZ = cz + Math.sin(a) * localR;
        }
      }
    }
  }

  /** After transitioning to spiral/tiers, sync cloud angle/distance with final positions */
  private syncAnglesAfterTransition(): void {
    for (let c = 0; c < this.nebula.children.length; c++) {
      const layer = this.nebula.children[c];
      if (!(layer instanceof THREE.Points) || !layer.geometry?.userData?.gasData) continue;

      const positions = layer.geometry.attributes.position.array as Float32Array;
      const gasData = layer.geometry.userData.gasData as GasCloudData[];

      for (let i = 0; i < gasData.length; i++) {
        const cloud = gasData[i];
        const x = positions[i * 3];
        const z = positions[i * 3 + 2];
        cloud.angle = Math.atan2(z, x);
        cloud.distance = Math.sqrt(x * x + z * z);
        cloud.baseY = positions[i * 3 + 1];
      }
    }
  }

  // --- Creation ---

  private createBackgroundStars(): void {
    const geometry = new THREE.BufferGeometry();
    const count = 8000;
    const positions = new Float32Array(count * 3);
    const colors = new Float32Array(count * 3);
    const sizes = new Float32Array(count);

    for (let i = 0; i < count; i++) {
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      const r = 400 + Math.random() * 600;

      positions[i * 3] = r * Math.sin(phi) * Math.cos(theta);
      positions[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
      positions[i * 3 + 2] = r * Math.cos(phi);

      const brightness = 0.5 + Math.random() * 0.5;
      colors[i * 3] = brightness;
      colors[i * 3 + 1] = brightness;
      colors[i * 3 + 2] = brightness + Math.random() * 0.2;
      sizes[i] = Math.random() * 1.5;
    }

    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    geometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1));

    const material = new THREE.ShaderMaterial({
      vertexShader: `
        attribute float size;
        attribute vec3 color;
        varying vec3 vColor;
        void main() {
          vColor = color;
          vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
          gl_PointSize = min(size * (200.0 / max(-mvPosition.z, 1.0)), 128.0);
          gl_Position = projectionMatrix * mvPosition;
        }
      `,
      fragmentShader: `
        varying vec3 vColor;
        void main() {
          float dist = length(gl_PointCoord - vec2(0.5));
          if (dist > 0.5) discard;
          float alpha = 1.0 - smoothstep(0.0, 0.5, dist);
          gl_FragColor = vec4(vColor, alpha * 0.6);
        }
      `,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });

    this.scene.add(new THREE.Points(geometry, material));
  }

  private createNebula(config: EngineConfig): THREE.Group {
    const nebula = new THREE.Group();
    const textureLoader = new THREE.TextureLoader();
    const cloudTexture = textureLoader.load(cloudTextureUrl);

    // Layer 1: smaller gas clouds
    this.createGasLayer(nebula, cloudTexture, config, {
      count: 450,
      sizeMultiplier: 1.1,
      spreadPow: 0.5,
      spread: 6,
      thicknessMultiplier: 1.2,
      hueMin: 0.665,
      hueRange: 0.335,
      saturation: 0.9,
      lightness: 0.5,
      lightnessRange: 0.5,
      particleSize: 20,
      opacity: 0.04,
      renderOrder: -1,
      baseSpeed: 0.00135,
      curveFactor: 1.0,
      breathSpeedMin: 0.05,
      breathSpeedRange: 0.08,
    });

    // Layer 2: larger gas clouds
    this.createGasLayer(nebula, cloudTexture, config, {
      count: 200,
      sizeMultiplier: 1.0,
      spreadPow: 0.4,
      spread: 10,
      thicknessMultiplier: 1.5,
      hueMin: 0.6,
      hueRange: 0.4,
      saturation: 0.8,
      lightness: 0.4,
      lightnessRange: 0.3,
      particleSize: 28,
      opacity: 0.03,
      renderOrder: -2,
      baseSpeed: 0.0015,
      curveFactor: 0.9,
      breathSpeedMin: 0.04,
      breathSpeedRange: 0.06,
    });

    this.scene.add(nebula);
    return nebula;
  }

  private createGasLayer(
    nebula: THREE.Group,
    cloudTexture: THREE.Texture,
    config: EngineConfig,
    opts: {
      count: number;
      sizeMultiplier: number;
      spreadPow: number;
      spread: number;
      thicknessMultiplier: number;
      hueMin: number;
      hueRange: number;
      saturation: number;
      lightness: number;
      lightnessRange: number;
      particleSize: number;
      opacity: number;
      renderOrder: number;
      baseSpeed: number;
      curveFactor: number;
      breathSpeedMin: number;
      breathSpeedRange: number;
    }
  ): void {
    const geometry = new THREE.BufferGeometry();
    const positions = new Float32Array(opts.count * 3);
    const colors = new Float32Array(opts.count * 3);
    const gasData: GasCloudData[] = [];

    for (let i = 0; i < opts.count; i++) {
      const armIndex = Math.floor(Math.random() * config.arms);
      const armAngle = (armIndex / config.arms) * Math.PI * 2;
      const t = Math.pow(Math.random(), opts.spreadPow);
      const distance = 3 + t * config.galaxyRadius * opts.sizeMultiplier;
      const angle =
        armAngle +
        t * config.armCurve * opts.curveFactor +
        (Math.random() - 0.5) * (opts.curveFactor < 1 ? 0.7 : 0.5);

      const x = Math.cos(angle) * distance + (Math.random() - 0.5) * opts.spread;
      const y = (Math.random() - 0.5) * config.thickness * opts.thicknessMultiplier * (1 - t * 0.3);
      const z = Math.sin(angle) * distance + (Math.random() - 0.5) * opts.spread;

      positions[i * 3] = x;
      positions[i * 3 + 1] = y;
      positions[i * 3 + 2] = z;

      const hue = opts.hueMin + Math.random() * opts.hueRange;
      const color = new THREE.Color().setHSL(
        hue,
        opts.saturation,
        opts.lightness + Math.random() * opts.lightnessRange
      );
      colors[i * 3] = color.r;
      colors[i * 3 + 1] = color.g;
      colors[i * 3 + 2] = color.b;

      gasData.push({
        angle,
        distance,
        baseY: y,
        origAngle: angle,
        origDistance: distance,
        origBaseY: y,
        rotationSpeed: opts.baseSpeed / Math.sqrt(0.3 + distance / config.galaxyRadius),
        breathPhase: Math.random() * Math.PI * 2,
        breathSpeed: opts.breathSpeedMin + Math.random() * opts.breathSpeedRange,
        transFromX: x,
        transFromY: y,
        transFromZ: z,
        transToX: x,
        transToY: y,
        transToZ: z,
      });
    }

    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    geometry.userData = { gasData };

    const material = new THREE.PointsMaterial({
      size: opts.particleSize,
      map: cloudTexture,
      transparent: true,
      opacity: opts.opacity,
      blending: THREE.NormalBlending,
      depthWrite: false,
      depthTest: true,
      vertexColors: true,
      sizeAttenuation: true,
    });

    const points = new THREE.Points(geometry, material);
    points.renderOrder = opts.renderOrder;
    nebula.add(points);
  }
}
